/**
 * Role identity, permissions, render props, and mutation callbacks for the
 * campaign-detail surface. The app page calls setCapabilities() once on mount;
 * every tab reads from this store instead of props or context. All callbacks
 * are nullable — tabs check before calling and disable UI when one is absent.
 */
import { create } from 'zustand';
import type { ReactNode } from 'react';
import type {
  Campaign,
  CampaignEditRequest,
  ContentSubmission,
  HoldReasonCode,
  ImportResult,
  AcceptanceRollbackFromStatus,
  RollbackFromStatus,
  RollbackReasonCode,
  ScriptDoc,
  SubmitEditRequestPayload,
} from '@discovr/types';
import type { GrantLevel } from '@discovr/api';
import type { MetricsRecheckResponse, ProfileRecheckResponse } from '../api/adminMutations';
// ─── Permission flags ─────────────────────────────────────────────────────────

export interface CampaignPermissions {
  // Creators tab
  canUploadCreatorSheet: boolean;
  canAddSingleCreator: boolean;
  canApproveRejectCreators: boolean;
  canApproveAcceptedCreators: boolean;
  canApproveOnBehalfOfBrand: boolean;
  canRemoveOnPending: boolean;
  /** Admin-only: Rejected tab's "send back to pending" action. */
  canReinstateRejected: boolean;
  /** Admin-only: Pending row's combined approve-as-brand + admin-approve +
   *  script-upload fast path ("Move to Script Review"). */
  canMoveToScriptReview: boolean;
  // Scripts tab
  canSubmitScriptOnBehalf: boolean;
  canReviewScript: boolean;
  /** Brand-only: final approve/accept of a submitted script (admin never approves). */
  canApproveScript: boolean;
  // Content tab
  canSubmitContentOnBehalf: boolean;
  canReviewContent: boolean;
  canEditLiveAnalytics: boolean;
  /** Admin-only: per-row "Send Brief" button on the Finalized tab. */
  canSendBrief: boolean;
  /** Admin-only: assign products to a creator (Multi-Product Campaigns). */
  canAssignProducts: boolean;
  canOpenConversation: boolean;
  /** Owner / super-admin: manage per-campaign access grants (Access tab). */
  canManageGrants?: boolean;
  /** Admin/staff only: view public share links (Settings → Links tab). Distinct
   *  from canManageGrants — brand roles manage grants but hold no
   *  campaign:share:read, so the Links tab 403s for them. */
  canViewShareLinks?: boolean;
  canEditCreator: boolean;
  /** Admin-only: put a post on hold / release it (Confirmed tab overflow menu). */
  canHoldCreator: boolean;
  /** Admin-only: roll an approved script/content back one review step. */
  canRollbackApproval: boolean;
  /** Brand and admin write: keep or change the products a rollback set aside. */
  canResolvePreviousProducts: boolean;
}

export interface HoldPostArgs {
  entryId: string;
  postId: string;
  reasonCode: HoldReasonCode;
  note?: string;
}

export interface ReleasePostArgs {
  entryId: string;
  postId: string;
  note?: string;
}

export interface RollbackApprovalArgs {
  entryId: string;
  postId: string;
  fromStatus: RollbackFromStatus;
  reasonCode: RollbackReasonCode;
  note?: string;
}

export interface RollbackAcceptanceArgs {
  entryId: string;
  postId: string;
  fromStatus: AcceptanceRollbackFromStatus;
  reasonCode: RollbackReasonCode;
  note?: string;
}

export interface RollbackBrandApprovalArgs {
  entryId: string;
  reasonCode: RollbackReasonCode;
  note?: string;
}

export interface ImportProgress {
  done: number;
  total: number;
  imported: number;
  rejected: number;
}

// ─── Store interface ──────────────────────────────────────────────────────────

export interface CampaignCapabilitiesState {
  role: 'admin' | 'admin_member' | 'brand_owner' | 'brand_admin' | 'brand_member' | null;
  /** True only for the Super Admin tier — deliberately NOT folded into `role`
   *  (which collapses super_admin into 'admin' for the rest of campaign-detail).
   *  Needed for the few checks where Super Admin has strictly more authority
   *  than a regular admin, e.g. bypassing a locked thread to delete a message.
   *  Always false on the brand lane. Required (not optional): setCapabilities
   *  shallow-merges, so an optional field a future caller forgot to pass would
   *  silently inherit whatever a PREVIOUS setCapabilities call in this store
   *  instance left it at, instead of resetting to a safe default. */
  viewerIsSuperAdmin: boolean;
  permissions: CampaignPermissions | null;
  /** Caller's effective per-campaign level (read|write|admin) for bespoke gating
   *  — deliverables selection, thread composer. UX only. */
  accessLevel: GrantLevel | null;
  /**
   * True once `role`/`permissions`/`accessLevel` reflect the actually-loaded
   * campaign (not mount-time defaults). `campaignId` resolves from an earlier,
   * separate fetch than these values, so a consumer waiting only on
   * `campaignId` could read the `null` defaults as "no access". Defaults
   * `false`; the authed page sets it `true` once its campaign-detail query
   * settles, the public share-view page immediately (static capabilities).
   */
  capabilitiesReady: boolean;
  /** Which app surface set these capabilities — 'admin' or 'brand'. Distinct from
   *  `role` (which names the specific tier): lane is the coarse app-side split used
   *  by cross-lane UI (e.g. the read-only "other lane" preview). */
  lane: 'admin' | 'brand' | null;

  // ── Render props (differ by role) ─────────────────────────────────────────
  renderEmbeddedBrandSafetyAgent: ((creator: Record<string, unknown>) => ReactNode) | null;
  renderEmbeddedContentReviewAgent: ((creator: Record<string, unknown>, onClose: () => void) => ReactNode) | null;
  renderEmbeddedScriptReviewAgent: ((creator: Record<string, unknown>, onClose: () => void) => ReactNode) | null;

  // ── Creator management callbacks ──────────────────────────────────────────
  /** Admin: opens EditCreatorDrawer for the given pending row. */
  onEditCreator: ((row: Record<string, unknown>) => void) | null;
  /** Brand/Admin: approve creator with selected deliverables. */
  approveCreatorAsBrand: ((creator: Record<string, unknown>, deliverables: string[]) => Promise<void>) | null;
  /** Both: reject a creator approval. */
  rejectCreatorApproval: ((creator: Record<string, unknown>, reason: string) => Promise<void>) | null;
  /**
   * Admin: permanently delete a pending creator's roster entry and every
   * scoped child row. Unlike rejectCreatorApproval this leaves nothing
   * behind — the creator does NOT land on the Rejected tab and cannot be
   * reinstated. `confirm` carries the typed-confirmation value
   * (ActionConfirmDialog's fixed-word gate) for the BE's confirm check.
   */
  deleteCreatorFromCampaign: ((creator: Record<string, unknown>, confirm: string) => Promise<void>) | null;
  /** Both: toggle a deliverable selection on a roster entry. */
  selectCreatorDeliverable: ((entryId: string, types: string[]) => Promise<void>) | null;
  /** Admin: reinstate a rejected creator back into the funnel. */
  reinstateCreator: ((creator: Record<string, unknown>) => Promise<void>) | null;
  /** Admin: put a post on hold / release it (creator-hold). */
  holdPost: ((args: HoldPostArgs) => Promise<void>) | null;
  releasePost: ((args: ReleasePostArgs) => Promise<void>) | null;
  /** Admin: roll an approval back one review step (approval rollback). */
  rollbackApproval: ((args: RollbackApprovalArgs) => Promise<void>) | null;
  /** Admin: Confirmed -> Brand approved (deletes the production record). */
  rollbackAcceptance: ((args: RollbackAcceptanceArgs) => Promise<void>) | null;
  /** Admin: Brand approved -> Pending (clears the deliverable selection). */
  rollbackBrandApproval: ((args: RollbackBrandApprovalArgs) => Promise<void>) | null;
  /**
   * Admin: approve as brand + move straight to script review with a pasted
   * script. Resolves `{stage}` on a partial mid-chain failure (BE left the
   * entry at a valid non-`script_review` status) instead of throwing — the
   * caller inspects `stage` to decide whether the row actually transitioned.
   */
  moveCreatorToScriptReview: ((creator: Record<string, unknown>, deliverables: string[], doc: ScriptDoc) => Promise<{ stage?: string } | void>) | null;
  /** Admin: approve and go live in one step (campaigns with no script/content stage). */
  approveCreatorAndGoLive: ((creator: Record<string, unknown>, deliverables: string[], url: string) => Promise<{ stage?: string } | void>) | null;
  /** Admin: approve and submit content in one step (campaigns with no script stage). */
  approveCreatorAndAddContent: ((creator: Record<string, unknown>, deliverables: string[], url: string) => Promise<{ stage?: string } | void>) | null;

  // ── Script callbacks ───────────────────────────────────────────────────────
  /** Admin: confirm a script upload for a roster entry. `doc` is the editor's
   *  native document (Lexical SerializedEditorState); the BE derives plain text
   *  from it server-side. */
  confirmScriptUpload: ((entryId: string, doc: ScriptDoc) => Promise<void>) | null;
  /** Admin: same route as confirmScriptUpload, explicit alias for the
   *  Confirmed-tab "add script one phase early" call sites. */
  onAddScript: ((entryId: string, doc: ScriptDoc) => Promise<void>) | null;
  /** Admin: approve or request changes on a submitted script. */
  handleAdminReviewScript: ((entryId: string, action: 'approve' | 'request_changes', feedback?: string) => Promise<void>) | null;
  /** Admin: trigger AI review run for a script. */
  handleRunAiScriptReview: ((entryId: string) => Promise<void>) | null;

  // ── Content callbacks ──────────────────────────────────────────────────────
  /** Admin: submit content on behalf of the creator. */
  handleAdminSubmitContent: ((entryId: string, payload: ContentSubmission) => Promise<void>) | null;
  /** Admin: approve or request changes on submitted content. */
  handleAdminReviewContent: ((entryId: string, action: 'approve' | 'request_changes', feedback?: string) => Promise<void>) | null;
  /** Admin: trigger AI review run for content. */
  handleRunAiContentReview: ((entryId: string) => Promise<void>) | null;
  /** Admin: set (or clear, when eta is null) the delivery ETA for a roster
   *  entry's script/content/live. `eta` is an ISO-8601 UTC instant. Brand app
   *  omits this (null) → ETA cells render read-only. */
  handleAdminSetEta: ((entryId: string, phase: 'script' | 'content' | 'live', eta: string | null) => Promise<void>) | null;
  /** Both: save live content analytics insight inputs. */
  handleSaveInsights: ((entryId: string, insights: Record<string, string>) => Promise<void>) | null;

  // ── Approval modal callback ────────────────────────────────────────────────
  /** Both: final approval step with selected deliverables (ApprovalModal). */
  confirmCreatorApproval: ((candidate: Record<string, unknown>, deliverables: string[]) => Promise<void>) | null;

  // ── Campaign detail callbacks ─────────────────────────────────────────────
  /** Admin: persist campaign field edits (platform, budget, etc.). */
  saveCampaignDetails: ((data: Record<string, unknown>) => Promise<void>) | null;

  // ── Edit-request review flow ──────────────────────────────────────────────
  /** Whether the caller may open the Campaign Details edit form at all —
   *  brand: write-or-higher access grant; admin: always true. */
  canEditCampaign: boolean;
  /** Brand only: submit a batch of proposed field changes for admin review. */
  submitCampaignEditRequest: ((payload: SubmitEditRequestPayload) => Promise<CampaignEditRequest>) | null;
  /** Admin only: "edit myself" bypass — applies immediately, no review. */
  editCampaignDirectly: ((payload: SubmitEditRequestPayload) => Promise<Campaign>) | null;
  /** Admin only: approve/reject/apply actions for the review panel. */
  reviewEditRequest: {
    approve: (requestId: string, fieldPath: string) => Promise<CampaignEditRequest>;
    reject: (requestId: string, fieldPath: string, reason: string) => Promise<CampaignEditRequest>;
    apply: (requestId: string) => Promise<CampaignEditRequest>;
  } | null;
  /** Both: post one entry to a field's comment thread, at any status — the
   *  BE gates on campaign:read (shared by brand + admin), not a role split. */
  addFieldComment: ((requestId: string, fieldPath: string, text: string) => Promise<CampaignEditRequest>) | null;

  // ── Reporting tab render prop ──────────────────────────────────────────────
  /** App-provided render prop for the full reporting overview. The app
   *  closure captures all needed data (campaign, KPIs, etc.) before registration. */
  renderReportingTab: (() => ReactNode) | null;

  // ── Other callbacks ────────────────────────────────────────────────────────
  /** Both: confirm roster brief received by creator. */
  handleConfirmRosterBrief: ((entryId: string) => Promise<void>) | null;
  /** Admin: set the live video URL for a roster entry. */
  handleSetLiveUrl: ((entryId: string, url: string) => Promise<void>) | null;

  /** Admin/ops: on-demand Official Audio Compliance recheck for a roster entry. */
  recheckAudioCompliance: ((entryId: string) => Promise<void>) | null;
  /** Admin: force-refresh one creator's Instagram profile (private → public). */
  recheckCreatorProfile: ((entryId: string) => Promise<ProfileRecheckResponse>) | null;
  /** Admin: force-refresh one creator's content metrics (Instagram reels, YouTube uploads). */
  recheckCreatorMetrics: ((entryId: string) => Promise<MetricsRecheckResponse>) | null;
  // ── Share-link capability ──────────────────────────────────────────────────
  /** Admin-only: when true, expose the copy-share-link button. Brand: false. */
  enableSharing: boolean;
  /** Absolute origin of the brand app used to build share deep links. */
  shareBrandBaseUrl: string | null;

  // ── SSE-driven AI review version counters ─────────────────────────────────
  aiScriptReviewVersion: Record<string, number>;
  aiContentReviewVersion: Record<string, number>;
  aiGeoVersion: Record<string, number>;

  // ── Sheet import result (set by useCampaignSSE on import.complete) ──────────
  importResult: ImportResult | null;
  // ── Sheet import live progress (cleared by import.complete or next upload) ──
  importProgress: ImportProgress | null;
  // ── Import wizard open (uploader's own flow) — suppresses the page-level
  //    import popup so the uploader isn't shown the same progress twice ───────
  importWizardOpen: boolean;

  // ── Store actions ─────────────────────────────────────────────────────────
  setCapabilities: (caps: Omit<CampaignCapabilitiesState, `set${string}` | `bump${string}` | 'aiScriptReviewVersion' | 'aiContentReviewVersion' | 'aiGeoVersion' | 'resetCapabilities' | 'importResult' | 'importProgress' | 'importWizardOpen'>) => void;
  bumpAiScriptReviewVersion: (entryId: string) => void;
  bumpAiContentReviewVersion: (entryId: string) => void;
  bumpAiGeoVersion: (entryId: string) => void;
  setImportResult: (result: ImportResult | null) => void;
  setImportProgress: (progress: ImportProgress | null) => void;
  setImportWizardOpen: (open: boolean) => void;
  resetCapabilities: () => void;
}

const initialCaps: Omit<CampaignCapabilitiesState, `set${string}` | 'bumpAiScriptReviewVersion' | 'bumpAiContentReviewVersion' | 'bumpAiGeoVersion' | 'setImportResult' | 'resetCapabilities'> = {
  role: null as ('admin' | 'admin_member' | 'brand_owner' | 'brand_admin' | 'brand_member' | null),
  viewerIsSuperAdmin: false,
  permissions: null,
  accessLevel: null,
  capabilitiesReady: false,
  lane: null as ('admin' | 'brand' | null),
  renderEmbeddedBrandSafetyAgent: null,
  renderEmbeddedContentReviewAgent: null,
  renderEmbeddedScriptReviewAgent: null,
  onEditCreator: null,
  confirmCreatorApproval: null,
  approveCreatorAsBrand: null,
  rejectCreatorApproval: null,
  deleteCreatorFromCampaign: null,
  selectCreatorDeliverable: null,
  reinstateCreator: null,
  holdPost: null,
  releasePost: null,
  rollbackApproval: null,
  rollbackAcceptance: null,
  rollbackBrandApproval: null,
  moveCreatorToScriptReview: null,
  approveCreatorAndGoLive: null,
  approveCreatorAndAddContent: null,
  onAddScript: null,
  confirmScriptUpload: null,
  handleAdminReviewScript: null,
  handleRunAiScriptReview: null,
  handleAdminSubmitContent: null,
  handleAdminReviewContent: null,
  handleRunAiContentReview: null,
  handleAdminSetEta: null,
  handleSaveInsights: null,
  saveCampaignDetails: null,
  canEditCampaign: false,
  submitCampaignEditRequest: null,
  editCampaignDirectly: null,
  reviewEditRequest: null,
  addFieldComment: null,
  renderReportingTab: null,
  handleConfirmRosterBrief: null,
  handleSetLiveUrl: null,
  recheckAudioCompliance: null,
  recheckCreatorProfile: null,
  recheckCreatorMetrics: null,
  enableSharing: false,
  shareBrandBaseUrl: null,
  aiScriptReviewVersion: {},
  aiContentReviewVersion: {},
  aiGeoVersion: {},
  importResult: null,
  importProgress: null,
  importWizardOpen: false,
};

/** The level of a view-only campaign grant. */
const READ_ONLY_ACCESS_LEVEL: GrantLevel = 'read';

/** Selector: the viewer holds only a read grant, so write controls are view-only. */
export const selectIsReadOnlyAccess = (s: Pick<CampaignCapabilitiesState, 'accessLevel'>): boolean =>
  s.accessLevel === READ_ONLY_ACCESS_LEVEL;

export const useCampaignCapabilitiesStore = create<CampaignCapabilitiesState>((set) => ({
  ...initialCaps,
  setCapabilities: (caps) => set(caps),
  bumpAiScriptReviewVersion: (entryId) =>
    set((s) => ({
      aiScriptReviewVersion: {
        ...s.aiScriptReviewVersion,
        [entryId]: (s.aiScriptReviewVersion[entryId] ?? 0) + 1,
      },
    })),
  bumpAiContentReviewVersion: (entryId) =>
    set((s) => ({
      aiContentReviewVersion: {
        ...s.aiContentReviewVersion,
        [entryId]: (s.aiContentReviewVersion[entryId] ?? 0) + 1,
      },
    })),
  bumpAiGeoVersion: (entryId) =>
    set((s) => ({
      aiGeoVersion: {
        ...s.aiGeoVersion,
        [entryId]: (s.aiGeoVersion[entryId] ?? 0) + 1,
      },
    })),
  setImportResult: (result) => set({ importResult: result }),
  setImportProgress: (progress) => set({ importProgress: progress }),
  setImportWizardOpen: (open) => set({ importWizardOpen: open }),
  resetCapabilities: () => set({ ...initialCaps }),
}));
